// BW.1.1 Search Web Accounts
import tippy from 'tippy.js';
import moment from 'moment';
import { ServerTable, Event } from 'vue-tables-2';
import { select, each } from '../shared/utils/dom';
import { api, activityLogApi } from './api';
import { delimiters } from '../shared/constants';

const el = select('[data-vue-app="search-web-accounts"]');

if (el) {
  // @see https://github.com/matfish2/vue-tables-2

  // start data
  const data = {};

  // the url
  data['url'] = `/NIS/CustomerAccount/1.0/Search`;

  // has loaded data for the first time?
  data['hasMounted'] = false;

  // if the table is loading data from the server
  data['loading'] = false;

  // start options
  const options = {};

  // filterable?
  options['filterable'] = false;
  options['uniqueKey'] = 'userId';

  options['headings'] = {
    primaryEmailAddress: 'Email',
    userId: 'User ID',
    name: 'Name',
    login: 'Last Login',
    impersonate: '',
  };

  options['columnsClasses'] = {
    impersonate: 'link-column',
  }

  options['rowClassCallback'] = function (row) {
    return row.active ? 'active' : '';
  };

  // get error message JSON block
  const errorMessages = document.getElementById('errors');
  if (errorMessages) {
    options['texts'] = {
    };
  }

  /**
   * Request Function to custom handle the Request
   * @param {object} _data
   */
  options.requestFunction = function (params) {
    if (!params.q || params.q.length < 3) {
      return Promise.resolve({
        data: {
          totalCount: 0,
          result: [],
        },
      });
    }

    const sortFieldLookup = {
      primaryEmailAddress: 'email',
      userId: 'user_id',
    };
    const sortDirection = params.ascending ? "asc" : "desc";
    const queryParams = {
      q: params.q,
      paging: `${params.page}-${params.limit}`,
      sortField: sortFieldLookup[params.orderBy],
      sortDirection,
    };
    return api.get(this.url, queryParams, false)
      .catch(function (e) { // eslint-disable-line prefer-arrow-callback
        this.loading = false;
        this.dispatch('error', e);
      }.bind(this));
  };

  /**
   * Adapt the response
   * @param {response} object
   */
  options.responseAdapter = function (response) { // eslint-disable-line object-shorthand
    if (typeof response === "undefined") {
      return;
    }

    const localData = [];
    const count = response.data.totalCount;

    each(response.data.result, (item) => {
      localData.push({
        primaryEmailAddress: item.primaryEmailAddress,
        userId: item.userId || item.primaryEmailAddress,
        name: `${item.firstName || ''} ${item.lastName || ''}`, // eslint-disable-line quote-props
        login: moment(item.lastLogin),
        impersonate: '/agent/impersonate',
        active: false,
        ldc: item.ldc,
      });
    });

    return { // eslint-disable-line consistent-return
      data: localData,
      count,
    };
  };

  // how many results per page?
  options.perPage = 10;

  // start templates
  const templates = {};

  /**
   * Template construction for "Account Nickname"
   */
  templates.primaryEmailAddress = function (h, row) {
    /* eslint-disable */
    return (
      <div class="table-cell-wrapper table-cell-header">
        <div class="table-cell-label">Email</div>
        <div class="table-cell-content">
          <span class="text-light-grey">{row.primaryEmailAddress}</span>
        </div>
      </div>
    );
    /* eslint-enable */
  };

  /**
   * Template for Due Date
   */
  templates.name = function (h, row) {
    return (
      <div class="table-cell-wrapper">
        <div class="table-cell-label">Name</div>
        <div class="table-cell-content">{row.name}</div>
      </div>
    );
  }

  /**
   * Template for Balance
   */
  templates.userId = function (h, row) {
    return (
      <div class="table-cell-wrapper">
        <div class="table-cell-label">User ID</div>
        <div class="table-cell-content">{row.userId}</div>
      </div>
    );
  }

  /**
   * Template for Payment Amount
   */
  templates.login = function (h, row) {
    return (
      <div class="table-cell-wrapper">
        <div class="table-cell-label">Last Login</div>
        <div class="table-cell-content">{row.login.isValid(row.login) ? row.login.format('MMM D, YYYY, h:mm A') : ''}</div>
      </div>
    );
  }

  /**
   * Template for Impersonation Link
   */
  templates.impersonate = function (h, row) { // eslint-disable-line no-unused-vars
    return (
      <div>
        <span
          class={{ 'activity-row': true }}
          style={{ cursor: 'pointer' }}
        >
          <a
            href={`${row.impersonate}?username=${encodeURIComponent(row.primaryEmailAddress)}`}
          >
            Impersonate
          </a>
        </span>
      </div>
    );
  }

  // set templates into options
  options['sortable'] = ['primaryEmailAddress', 'userId'];
  options['templates'] = templates;
  options['customFilters'] = ['q'];
  options['childRow'] = (h, row) => {
    if (!row.active) {
      return null;
    }
    if (app.activityHistory[row.userId] && app.activityHistory[row.userId].length > 0) { // eslint-disable-line no-use-before-define
      return (
        <div>
          <table>
            <thead>
              <td>Date</td>
              <td>Customer Account</td>
              <td>Actor</td>
              <td>Action</td>
            </thead>
            <tbody>
              {app.activityHistory[row.userId].map(item => // eslint-disable-line no-use-before-define
                <tr>
                  <td>
                    <div class="table-cell-wrapper table-cell-header">
                      <div class="table-cell-label">
                        Date
                      </div>
                      <div class="table-cell-content">
                        {moment(item.endTime).format('MMM D, YYYY hh:mm:ssA')}
                      </div>
                    </div>
                  </td>
                  <td>
                    <div class="table-cell-wrapper table-cell-header">
                      <div class="table-cell-label">
                        Customer Account
                      </div>
                      <div class="table-cell-content">
                        {item.accountId}
                      </div>
                    </div>
                  </td>
                  <td>
                    <div class="table-cell-wrapper table-cell-header">
                      <div class="table-cell-label">
                        Actor
                      </div>
                      <div class="table-cell-content">
                        {item.actor}
                      </div>
                    </div>
                  </td>
                  <td>
                    <div class="table-cell-wrapper table-cell-header">
                      <div class="table-cell-label">
                        Action
                      </div>
                      <div class="table-cell-content">
                        <span class="text-md">{item.activityDescription}<br />{item.activityStatus}<br /></span>
                        <span class="text-xs">{item.activityDescription}</span>
                      </div>
                    </div>
                  </td>
                </tr>,
              )}
            </tbody>
          </table>
          <a class="impersonate"
            href={`/agent/activity?username=${encodeURIComponent(row.primaryEmailAddress)}`}>
            View All Activity
          </a>
        </div>
      );
    }
    return (
      <span class="no-activity">No Recent Activity</span>
    );
  }
  options['childRowTogglerFirst'] = false;

  // set options into data
  data['options'] = options;
  data['activityHistory'] = {};

  data['columns'] = ['primaryEmailAddress', 'userId', 'name', 'login', 'impersonate'];
  data['tableData'] = [];
  data['clientNumber'] = '';
  data['filterTable'] = '';

  Vue.use(ServerTable);

  const app = new Vue({
    el,
    delimiters,
    data() {
      return data;
    },
    methods: {
      applyFilter() {
        Event.$emit('vue-tables.filter::q', this.filterTable);
      },
    },
  });

  app.loading = true;

  // this will remove the initial loading state and show the table
  Event.$on('vue-tables.loaded', () => {
    app.hasMounted = true;
    app.loading = false;

    // tooltip functionality for actions
    tippy('.table-container', {
      arrow: true,
      delay: 300, // delay for user intent
      flip: true,
      theme: 'nisource',
      target: '.tooltip',
      trigger: 'mouseenter focus',
    });
  });

  // this will remove the initial loading state and show the table
  Event.$on('vue-tables.loading', () => {
    app.loading = true;
  });

  // do this when the child row is opened for the first time
  Event.$on('vue-tables.row-click', (e) => {
    // if user clicks on impersonate, don't do any of this
    if (e.event.target.tagName !== 'A') {
      if (e.row.active === false) {
        // repeating search doesn't seem to toggle child rows
        // so let's make sure that when the row is opened, the child row is toggled off first
        const openChildren = app.$refs.webAccountTable.openChildRows;
        if (openChildren.includes(e.row.userId)) {
          app.$refs.webAccountTable.toggleChildRow(e.row.userId);
        }

        app.loading = true;
        // get account activity on row click
        // saved to this.activityHistory
        activityLogApi.getLatestActivityForAccount(e.row.userId, null, null, null, 10, 0)
          .then((response) => {
            app.activityHistory[e.row.userId] = response.data.result
              .map(item => ({
                accountId: item.accountId,
                actor: item.emailAddress,
                activityDescription: item.activityDescription,
                activityStatus: item.activityStatus,
                endTime: item.endTime,
              }));
            app.$refs.webAccountTable.toggleChildRow(e.row.userId);
            e.row.active = true;
            app.loading = false;
          });
      } else {
        e.row.active = false;
        app.$refs.webAccountTable.toggleChildRow(e.row.userId);
      }
    }
  });

  // this will remove the initial loading state and show the table
  Event.$on('vue-tables.error', () => {
    app.hasMounted = true;
    app.loading = false;
  });
}



// WEBPACK FOOTER //
// ./src/assets/toolkit/scripts/portal/search-web-accounts.js